/* eslint-disable no-underscore-dangle */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import { fetchElementoById, fetchElementoByIdGrafica, limpiarElemento } from '../../../actions/elemento';
import AccordionObjetivos from '../../../components/acordion-objetivos';
import Bread from '../../../components/breadcrumb';
import Tab from '../../../components/tab';
import { routeElementoInfo } from '../../../utils/routesBread';
import AccordionElemento from '../accordion-elemento';
import InfoCard from '../../../components/info-card';
import SankeyContainer from '../../../components/sankey/SankeyContainer';

const InfoElemento = () => {
  const { elemento, isLoading } = useSelector((state) => state.elementoReducer);
  const [elementoData, setElementoData] = useState(null);
  const { id } = useParams();
  const dispatch = useDispatch();

  const updateObjetivos = () => {
    dispatch(fetchElementoById(id));
  };

  useEffect(() => {
    dispatch(fetchElementoById(id));
  }, [id, dispatch]);

  useEffect(() => {
    // Extraer los datos correctos considerando la estructura de respuesta
    let datos = null;
    if (elemento && elemento.data) {
      datos = elemento.data;
    } else if (elemento && !elemento.data) {
      datos = elemento;
    }
    
    setElementoData(datos);
  }, [elemento]);

  useEffect(() => {
    const intervalId = setInterval(
      () => dispatch(fetchElementoByIdGrafica(id)),
      30000
    );
    return () => {
      clearInterval(intervalId);
    };
  }, [id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(limpiarElemento());
    };
  }, [dispatch]);

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!elementoData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No se encontraron datos del elemento
        </Typography>
      </Box>
    );
  }

  // Preparar información básica del elemento
  const elementoBasicInfo = [
    { label: 'Nombre', value: elementoData.nombre || 'N/A' },
    { label: 'Descripción', value: elementoData.descripcion || 'Sin descripción' },
    { label: 'Tipo', value: elementoData.tipo || 'N/A' },
    { label: 'Proyecto', value: elementoData.proyecto?.nombre || 'N/A' },
    { label: 'Estado', value: elementoData.activo ? 'Activo' : 'Inactivo' },
    { label: 'Fecha de creación', value: elementoData.fechaCreacion ? new Date(elementoData.fechaCreacion).toLocaleDateString() : 'N/A' }
  ];

  return (
    <div>
      <div className="header-nav">
        <Bread routes={routeElementoInfo} />
      </div>
      
      <Box sx={{ p: 3 }}>
        {/* Información básica del elemento */}
        <InfoCard
          title="Información del Elemento"
          subtitle={`${elementoData.tarjetas?.length || 0} tarjetas configuradas`}
          icon={<AccountTreeIcon fontSize="large" />}
          items={elementoBasicInfo}
          variant="elevation"
        />
        
        {/* Acordeón de objetivos */}
        {elementoData?.objetivos && elementoData.objetivos.length > 0 && (
          <MuiAccordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
              sx={{ 
                backgroundColor: '#1F2A38', 
                color: 'white',
                '&:hover': {
                  backgroundColor: '#303C54',
                },
                '& .MuiTypography-root': {
                  color: 'white',
                },
                '& .MuiSvgIcon-root': {
                  color: 'white',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountTreeIcon />
                <Typography variant="h6">
                  Objetivos ({elementoData.objetivos.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <AccordionObjetivos
                dataSource={elementoData?.objetivos}
                isLoading={isLoading}
                onUpdated={updateObjetivos}
              />
            </AccordionDetails>
          </MuiAccordion>
        )}
        
        {/* Acordeón de tarjetas */}
        <AccordionElemento data={elementoData} />
        
        {/* Nueva sección de Gráfica Sankey del Elemento */}
        <MuiAccordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
            sx={{ 
              backgroundColor: '#1F2A38', 
              color: 'white',
              '&:hover': {
                backgroundColor: '#303C54',
              },
              '& .MuiTypography-root': {
                color: 'white',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon />
              <Typography variant="h6">
                Análisis de Flujo de Datos (Sankey)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <SankeyContainer
              type="elemento"
              id={id}
              title={`Flujo de Datos - ${elementoData.nombre || 'Elemento'}`}
              showControls={true}
              initialParams={{
                limite: 60,
                unidades: [],
                incluirInactivos: false
              }}
              controlsConfig={{
                showUnidadesFilter: true,
                showLimiteControl: true,
                showInactivosToggle: true
              }}
            />
          </AccordionDetails>
        </MuiAccordion>
        
        {/* Acordeón de gráficas */}
        <MuiAccordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
            sx={{ 
              backgroundColor: '#1F2A38', 
              color: 'white',
              '&:hover': {
                backgroundColor: '#303C54',
              },
              '& .MuiTypography-root': {
                color: 'white',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShowChartIcon />
              <Typography variant="h6">
                Gráficas {elementoData?.graficas ? `(${elementoData.graficas.length})` : '(0)'}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            {(elementoData?.graficas || elementoData?.graficasSankey) ? (
              <Tab
                dataGrafica={elementoData?.graficas}
                dataSankey={elementoData?.graficasSankey}
              />
            ) : (
              <InfoCard
                title="Sin gráficas configuradas"
                subtitle="No hay gráficas asociadas a este elemento"
                icon={<ShowChartIcon />}
                items={[]}
                chips={[{ label: 'Sin configurar', color: 'default', variant: 'outlined' }]}
              />
            )}
          </AccordionDetails>
        </MuiAccordion>
      </Box>
    </div>
  );
};

export default InfoElemento;